import {type ChangeSpec, EditorSelection} from '@codemirror/state'
import type {EditorView} from '@codemirror/view'
import type {Delimiter, DelimiterParent, Literal, Nodes} from 'mdast'

import {isPositionedNode, type PositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getCurrentSelectionContext} from '../../extensions/selection-context'
import {createText} from '../../extensions/single-line-editor'
import {findWordBoundaries, hasNonEmptySelection} from '../../utils'
import {type FormatInlineNodeType, NODE_TYPE_TO_INLINE_DELIMITER_MAP} from './constants'

/**
 * Checks if the current selection has existing formatting of the specified type
 */
export const getExistingFormatting = (view: EditorView, type: string): Nodes | undefined => {
  const selectionContext = getCurrentSelectionContext(view.state)
  return selectionContext.findLast(node => node.type === type)
}

/**
 * Collect all delimiter children in a node
 */
export const getDelimiters = (node: DelimiterParent): Delimiter[] => {
  if (!node.children) return []
  return node.children.filter((child): child is Delimiter => child.type === 'delimiter')
}

/**
 * Removes formatting delimiters around the current selection or cursor position.
 */
export const removeFormattingAtSelection = (view: EditorView, node: Nodes) => {
  const delimiters = getDelimiters(node as DelimiterParent).filter(isPositionedNode) as PositionedNode[]
  if (delimiters.length === 0) return

  const changes: ChangeSpec[] = delimiters.map(delimiter => ({
    from: delimiter.position.start.offset,
    to: delimiter.position.end.offset,
  }))

  view.dispatch({
    changes,
    userEvent: 'input',
    scrollIntoView: true,
  })
}

/*
 * Put "<prefix>" before the selection range and "<suffix>" after the selection range
 */
export const wrapSelectionWithDelimiter = (view: EditorView, prefix: string, suffix: string) => {
  view.dispatch(
    view.state.changeByRange(range => ({
      changes: [
        {from: range.from, insert: prefix},
        {from: range.to, insert: suffix},
      ],
      range: EditorSelection.range(range.from + prefix.length, range.to + prefix.length),
    })),
    {userEvent: 'input', scrollIntoView: true},
  )
}

/**
 * Finds the relevant word node from the selection context
 */
const findWordNodeInContext = (selectionContext: Nodes[]) => selectionContext.find(node => node.type === 'text')

/*
 * Inserts formatting delimiters around the word at the current cursor position.
 *
 * When no text is selected, this function places the prefix and suffix delimiters around
 * the word containing the cursor. For nested formatting (e.g., adding bold to text that's
 * already italic), it places the new delimiters inside the existing ones
 * by using word boundaries within the current formatting node.
 *
 * Example: cursor in `_wo|rd_` + bold formatting → `_**word**_`
 */
const insertDelimiterAtSelection = (view: EditorView, prefix: string, suffix: string, wordNode?: Literal) => {
  const caretLocation = view.state.selection.main.anchor

  // Determine text and offset for word boundary calculation
  const hasWordNode = wordNode?.value && isPositionedNode(wordNode)
  const {text, offset} = hasWordNode
    ? {text: wordNode.value, offset: caretLocation - wordNode.position.start.offset}
    : {text: view.state.doc.toString(), offset: caretLocation}
  const wordNodeStart = hasWordNode ? wordNode.position.start.offset : 0
  const {start: wordStart, end: wordEnd} = findWordBoundaries(text, offset)

  view.dispatch({
    changes: [
      {from: wordStart + wordNodeStart, insert: createText(prefix)},
      {from: wordEnd + wordNodeStart, insert: createText(suffix)},
    ],
    // Set new caret position inside the newly added delimiters because default CodeMirror behavior fails on complex insertions.
    selection: {anchor: caretLocation + prefix.length},
    userEvent: 'input',
    scrollIntoView: true,
  })
}

/**
 * Generic formatter function that handles inline formatting types
 *
 * This function either removes existing formatting of the specified type
 * or applies new formatting to the current selection/cursor position.
 */
export const inlineFormatter = (view: EditorView, type: FormatInlineNodeType): boolean => {
  if (!view.hasFocus) view.focus()

  const existingFormattedNode = getExistingFormatting(view, type)

  if (existingFormattedNode) {
    removeFormattingAtSelection(view, existingFormattedNode)
    return true
  }

  // Apply formatting to selection or word at cursor
  const delimiter = NODE_TYPE_TO_INLINE_DELIMITER_MAP[type]
  if (hasNonEmptySelection(view.state)) {
    wrapSelectionWithDelimiter(view, delimiter, delimiter)
  } else {
    const selectionContext = getCurrentSelectionContext(view.state)
    const wordNode = findWordNodeInContext([...selectionContext])
    insertDelimiterAtSelection(view, delimiter, delimiter, wordNode)
  }

  return true
}
